import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TuneIcon from '@mui/icons-material/Tune';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import GridViewIcon from '@mui/icons-material/GridView';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import CasinoIcon from '@mui/icons-material/Casino';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CampaignIcon from '@mui/icons-material/Campaign';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ArticleIcon from '@mui/icons-material/Article';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import SchoolIcon from '@mui/icons-material/School';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CategoryIcon from '@mui/icons-material/Category';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import DescriptionIcon from '@mui/icons-material/Description';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import InsightsIcon from '@mui/icons-material/Insights';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  ADMIN_CONTROL_SECTIONS,
  userCanAccessAdminControlItem,
} from '../../config/adminControlScreens';

/** MUI icon map — vector icons, no image assets / token cost. */
const CONTROL_ICONS = {
  Storefront: StorefrontIcon,
  PhoneAndroid: PhoneAndroidIcon,
  GridView: GridViewIcon,
  DashboardCustomize: DashboardCustomizeIcon,
  Newspaper: NewspaperIcon,
  Casino: CasinoIcon,
  RocketLaunch: RocketLaunchIcon,
  LocalOffer: LocalOfferIcon,
  Campaign: CampaignIcon,
  HowToReg: HowToRegIcon,
  PostAdd: PostAddIcon,
  Article: ArticleIcon,
  WorkOutline: WorkOutlineIcon,
  School: SchoolIcon,
  PointOfSale: PointOfSaleIcon,
  Category: CategoryIcon,
  ListAlt: ListAltIcon,
  Inventory2: Inventory2Icon,
  Description: DescriptionIcon,
  QrCode2: QrCode2Icon,
  Chat: ChatIcon,
  Settings: SettingsIcon,
  LockOpen: LockOpenIcon,
  NotificationsActive: NotificationsActiveIcon,
  PriceChange: PriceChangeIcon,
  WorkspacePremium: WorkspacePremiumIcon,
  AccountBalance: AccountBalanceIcon,
  AccountBalanceWallet: AccountBalanceWalletIcon,
  CardGiftcard: CardGiftcardIcon,
  HowToVote: HowToVoteIcon,
  Insights: InsightsIcon,
};

function ControlThumb({ icon, tint = '#64748b' }) {
  const Icon = CONTROL_ICONS[icon] || TuneIcon;
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 1.5,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${tint}18`,
        color: tint,
        border: `1px solid ${tint}33`,
      }}
    >
      <Icon sx={{ fontSize: 26 }} />
    </Box>
  );
}

const AdminControlScreens = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ADMIN_CONTROL_SECTIONS.map((section) => {
      const items = section.items.filter((item) => {
        if (!userCanAccessAdminControlItem(user, item)) return false;
        if (!q) return true;
        const hay = [
          item.title,
          item.description,
          item.path,
          ...(item.keywords || []),
          section.title,
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
      return { ...section, items };
    }).filter((section) => section.items.length > 0);
  }, [user, query]);

  const totalVisible = sections.reduce((n, s) => n + s.items.length, 0);

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack spacing={1} mb={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TuneIcon color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Admin Control Screens
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One place for screens, approvals, wheel, ads & platform controls (AA login)
              </Typography>
            </Box>
          </Stack>

          <TextField
            size="small"
            placeholder="Search controls (apptrends, loksyhomescreen_controls, mitra, wheel, approvals…)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ maxWidth: 480, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {totalVisible} control{totalVisible === 1 ? '' : 's'} available for your role
          </Typography>
        </Stack>

        {sections.length === 0 ? (
          <Typography color="text.secondary">
            {user?.role === 'app_operator'
              ? 'No Admin Control Screens are granted for your account. Ask App Admin to enable them under My Operators → Edit Permissions.'
              : 'No controls match your search.'}
          </Typography>
        ) : (
          <Stack spacing={4}>
            {sections.map((section) => (
              <Box key={section.id}>
                <Stack direction="row" spacing={1} alignItems="baseline" mb={1.5}>
                  <Typography variant="h6" fontWeight={700}>
                    {section.title}
                  </Typography>
                  <Chip size="small" label={`${section.items.length}`} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {section.subtitle}
                </Typography>
                <Grid container spacing={2}>
                  {section.items.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.path}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          transition: 'border-color 0.15s, box-shadow 0.15s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardActionArea
                          onClick={() => navigate(item.path)}
                          sx={{ height: '100%', alignItems: 'stretch' }}
                        >
                          <CardContent sx={{ height: '100%' }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              <ControlThumb icon={item.icon} tint={item.tint} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="flex-start"
                                  spacing={1}
                                >
                                  <Typography variant="subtitle1" fontWeight={700}>
                                    {item.title}
                                  </Typography>
                                  <ArrowForwardIcon fontSize="small" color="action" />
                                </Stack>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, minHeight: 40 }}
                                >
                                  {item.description}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ mt: 1.25, display: 'block' }}
                                >
                                  {item.path}
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Stack>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default AdminControlScreens;
